"use client"

import { ChevronRight, Plus, Search, Telescope } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * What an empty pipeline stage says, from canvas 18c.
 *
 * "'Nothing in longlist.' becomes a sentence with the fact that matters (the
 * role is 12 days old) and three ways out, ranked, each stating what it will
 * get you."
 *
 * 18c also prints a pool count and the last three longlists on this client
 * under those routes. Neither has an endpoint behind it, so the sublines here
 * say what the route does rather than inventing a figure.
 */

export type StageRoute = {
  id: string
  label: string
  detail: string
  icon: typeof Search
  /** The first route is the one to take; the canvas draws it in brand ink. */
  primary?: boolean
  onSelect: () => void
}

export function stageRoutes({
  onSearchPool,
  onAddByName,
  onNewSearch,
}: {
  onSearchPool: () => void
  onAddByName: () => void
  onNewSearch: () => void
}): StageRoute[] {
  return [
    {
      id: "pool",
      label: "Search the talent pool",
      detail: "People you have already sourced",
      icon: Search,
      primary: true,
      onSelect: onSearchPool,
    },
    {
      id: "name",
      label: "Add someone by name",
      detail: "If you already know who you want",
      icon: Plus,
      onSelect: onAddByName,
    },
    {
      id: "search",
      label: "Run a new search",
      detail: "Sources outside the pool",
      icon: Telescope,
      onSelect: onNewSearch,
    },
  ]
}

export function EmptyStage({
  stage,
  openedFact,
  routes,
}: {
  stage: string
  /** "Northwind opened this role 12 days ago", when the job carries a date. */
  openedFact?: string
  routes: StageRoute[]
}) {
  return (
    <div>
      <p className="pt-5 text-[14px] leading-[1.7] text-foreground">
        Nothing in the {stage} yet.{openedFact ? ` ${openedFact}` : ""}
      </p>

      <div className="mt-[18px]">
        {routes.map((route) => (
          <button
            key={route.id}
            type="button"
            onClick={route.onSelect}
            className="flex w-full items-center gap-3 border-t py-[15px] text-left"
          >
            <route.icon
              className={cn(
                "h-4 w-4 flex-none",
                route.primary ? "text-brand-ink" : "text-muted-foreground",
              )}
            />
            <span className="min-w-0 flex-1">
              <span
                className={cn(
                  "block text-[13.5px] leading-[1.3]",
                  route.primary ? "font-medium text-brand-ink" : "text-foreground",
                )}
              >
                {route.label}
              </span>
              <span className="mt-[5px] block text-[11px] leading-[1.4] text-muted-foreground">
                {route.detail}
              </span>
            </span>
            <ChevronRight className="h-4 w-4 flex-none text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * How long the role has been open, as a sentence. The pipeline row's age is not
 * the job's, so this reads `posted_at` — the only date the job carries.
 */
export function openedFact(company?: string, postedAt?: string) {
  if (!postedAt) return undefined
  const posted = new Date(postedAt)
  if (Number.isNaN(posted.getTime())) return undefined

  const days = Math.floor((Date.now() - posted.getTime()) / 86400000)
  if (days < 0) return undefined

  const who = company || "The client"
  if (days === 0) return `${who} opened this role today.`
  if (days === 1) return `${who} opened this role yesterday.`
  return `${who} opened this role ${days} days ago.`
}
